import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, FlaskConical, RefreshCw, Sparkles } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageLayout } from '@/components/layout/PageLayout';
import { tabService, type Pattern as ApiPattern } from '../services/tabs';
import { resolveWorkspaceSessionId } from '@/services/workspaceSession';
import {
  derivePatternsFromTabs,
  mergeTabPatterns,
  parsePatternFrequency,
  patternsThisWeek,
  type TabPatternView,
} from '../utils/tabPatterns';

function mapApiPattern(p: ApiPattern): TabPatternView {
  return {
    id: p.id,
    name: p.name,
    frequency: parsePatternFrequency(p.frequency),
    category: 'Learned',
    confidence: p.confidence,
    trend: p.trend === 'increasing' ? 'up' : p.trend === 'decreasing' ? 'down' : 'stable',
    lastDetected: p.lastOccurred ? new Date(p.lastOccurred) : new Date(),
    description: p.name,
  };
}

const PatternDetectionViewer: React.FC = () => {
  const [selectedPattern, setSelectedPattern] = useState<TabPatternView | null>(null);
  const [patterns, setPatterns] = useState<TabPatternView[]>([]);
  const [tabCount, setTabCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadPatterns = useCallback(async () => {
    setIsLoading(true);
    try {
      const sessionId = await resolveWorkspaceSessionId();
      const [apiResult, tabsResult] = await Promise.allSettled([
        tabService.getDetectedPatterns(),
        sessionId
          ? tabService.getTabsBySession(sessionId, 1, 100)
          : Promise.resolve({ tabs: [], total: 0 }),
      ]);

      const apiPatterns =
        apiResult.status === 'fulfilled' ? apiResult.value.map(mapApiPattern) : [];

      const tabs = tabsResult.status === 'fulfilled' ? tabsResult.value.tabs : [];
      setTabCount(tabs.length);

      const derived = derivePatternsFromTabs(
        tabs.map((tab) => ({
          url: tab.url,
          title: tab.title,
          updated_at: tab.updated_at,
        })),
      );

      setPatterns(mergeTabPatterns(apiPatterns, derived));
    } catch (err) {
      console.error('Failed to load patterns', err);
      setPatterns([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPatterns();
  }, [loadPatterns]);

  const stats = useMemo(
    () => ({
      total: patterns.length,
      highConfidence: patterns.filter((p) => p.confidence >= 75).length,
      trendingUp: patterns.filter((p) => p.trend === 'up').length,
      thisWeek: patternsThisWeek(patterns),
    }),
    [patterns],
  );

  return (
    <PageLayout
      title="Pattern Detection"
      description="Patterns detected from your open tabs and browsing history"
      icon={BarChart3}
      actions={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void loadPatterns()}
          disabled={isLoading}
          leftIcon={<RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />}
        >
          Refresh
        </Button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Patterns Found', value: stats.total, bgClass: 'bg-primary-500' },
          { label: 'High Confidence', value: stats.highConfidence, bgClass: 'bg-success-500' },
          { label: 'Trending Up', value: stats.trendingUp, bgClass: 'bg-info-500' },
          { label: 'This Week', value: stats.thisWeek, bgClass: 'bg-khiimori-accent-fuchsia' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card hover>
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${stat.bgClass}`}>
                  <BarChart3 className="h-5 w-5 text-khiimori-text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-khiimori-text-primary">{stat.value}</div>
                  <div className="text-xs text-khiimori-text-secondary">{stat.label}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {isLoading && patterns.length === 0 ? (
        <Card className="p-8 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
        </Card>
      ) : patterns.length === 0 ? (
        <Card className="p-6 text-sm text-khiimori-text-muted">
          {tabCount === 0
            ? 'No patterns yet. Open a few tabs and refresh to see live session patterns.'
            : 'No patterns detected from your current tabs.'}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {patterns.map((pattern, index) => (
            <motion.div
              key={pattern.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setSelectedPattern(pattern)}
              className="cursor-pointer"
            >
              <Card hover className={selectedPattern?.id === pattern.id ? 'ring-2 ring-primary-500' : ''}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="p-3 bg-primary-500 rounded-xl">
                      <FlaskConical className="h-6 w-6 text-khiimori-text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-khiimori-text-primary">{pattern.name}</h3>
                      <span className="px-2 py-0.5 bg-primary-500/20 dark:bg-primary-500/30 text-primary-500 dark:text-primary-400 rounded text-xs">
                        {pattern.category}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      pattern.trend === 'up'
                        ? 'bg-success-500/20 text-success-500 dark:bg-success-500/30 dark:text-success-400'
                        : pattern.trend === 'down'
                          ? 'bg-error-500/20 text-error-500 dark:bg-error-500/30 dark:text-error-400'
                          : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary'
                    }`}
                  >
                    {pattern.trend === 'up' ? '↑' : pattern.trend === 'down' ? '↓' : '→'} {pattern.trend}
                  </div>
                </div>
                <p className="text-khiimori-text-secondary mb-4">{pattern.description}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-khiimori-text-muted mb-1">Frequency</div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-2 bg-khiimori-bg-tertiary rounded-full overflow-hidden">
                        <div className="h-full bg-primary-500" style={{ width: `${pattern.frequency}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-khiimori-text-primary">
                        {pattern.frequency}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-khiimori-text-muted mb-1">Confidence</div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-2 bg-khiimori-bg-tertiary rounded-full overflow-hidden">
                        <div className="h-full bg-success-500" style={{ width: `${pattern.confidence}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-khiimori-text-primary">
                        {pattern.confidence}%
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8">
        <Card className="bg-gradient-glass border-primary-400/30 dark:border-primary-400/20">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary-500 rounded-xl">
              <Sparkles className="h-6 w-6 text-khiimori-text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-khiimori-text-primary">AI Pattern Analysis</h3>
              <p className="text-sm text-khiimori-text-secondary">
                {tabCount > 0
                  ? `Analyzing ${tabCount} open tab${tabCount === 1 ? '' : 's'} plus learned behaviour patterns.`
                  : 'Machine learning continuously analyzes your browsing patterns to provide insights.'}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

export default PatternDetectionViewer;
